import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { confirmAgent, createAgent, listAgents, sendAgentMessage } from '../../../helpers/agents';

export default function useMessageComposer({
  selectedAgentId,
  selectedModelId,
  fetchDetail,
  setPollingNonce,
  setAgents,
  setSelectedAgentId,
  setCreatingNewAgent,
}) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creationMessages, setCreationMessages] = useState([]);

  const [quotes, setQuotes] = useState([]);
  const nextQuoteIdRef = useRef(0);
  const cursorPositionRef = useRef(0);
  const chatInputRef = useRef(null);

  const [documentCropAttachment, setDocumentCropAttachment] = useState(null);

  const handleQuote = useCallback((quoteData) => {
    setQuotes((prev) => {
      if (prev.some((q) => q.text === quoteData.text)) return prev;

      const quoteId = nextQuoteIdRef.current;
      nextQuoteIdRef.current += 1;

      const newQuote = { ...quoteData, id: quoteId };
      const marker = `⟦${quoteId}⟧`;
      const cursorPos = cursorPositionRef.current || 0;

      setMessage((prevMessage) => {
        const before = prevMessage.slice(0, cursorPos);
        const after = prevMessage.slice(cursorPos);
        return `${before}${marker}${after}`;
      });

      setTimeout(() => { chatInputRef.current?.focus(); }, 0);

      return [...prev, newQuote];
    });
  }, []);

  const handleRemoveQuote = useCallback((quoteId) => {
    setQuotes((prev) => prev.filter((q) => q.id !== quoteId));
  }, []);

  const handleDocumentTextQuote = useCallback((quoteData) => {
    setQuotes((prev) => {
      const quoteId = nextQuoteIdRef.current;
      nextQuoteIdRef.current += 1;

      const newQuote = {
        id: quoteId,
        text: quoteData.text,
        source: 'file',
        documentName: quoteData.documentName,
        page: quoteData.page,
      };

      const marker = `⟦${quoteId}⟧`;
      const cursorPos = cursorPositionRef.current || 0;

      setMessage((prevMessage) => {
        const before = prevMessage.slice(0, cursorPos);
        const after = prevMessage.slice(cursorPos);
        return `${before}${marker}${after}`;
      });

      setTimeout(() => { chatInputRef.current?.focus(); }, 0);

      return [...prev, newQuote];
    });
  }, []);

  const handleDocumentRegionCrop = useCallback((cropData) => {
    setDocumentCropAttachment({
      blob: cropData.imageBlob,
      dataUrl: cropData.imageDataUrl,
      documentName: cropData.documentName,
      page: cropData.page,
      bounds: cropData.bounds,
    });
  }, []);

  const handleRemoveCropAttachment = useCallback(() => {
    setDocumentCropAttachment(null);
  }, []);

  const handleSendMessage = async () => {
    if (!message.trim() || !selectedAgentId) return;
    try {
      setSending(true);

      let documentBase64 = null;
      if (documentCropAttachment?.dataUrl) {
        documentBase64 = documentCropAttachment.dataUrl;
      }

      let finalMessage = message.trim();
      const quotesMap = new Map();
      quotes.forEach((q) => quotesMap.set(q.id, q));

      finalMessage = finalMessage.replace(/⟦(\d+)⟧/g, (match, idStr) => {
        const quoteId = parseInt(idStr, 10);
        const quote = quotesMap.get(quoteId);
        if (!quote) return '';

        let sourceLabel;
        if (quote.source === 'file' && quote.documentName) {
          sourceLabel = quote.page
            ? `${quote.documentName}, p.${quote.page}`
            : quote.documentName;
        } else {
          sourceLabel = {
            user: 'user message',
            assistant: 'assistant',
            policy: 'policy',
            file: 'file',
          }[quote.source] || 'quote';
        }

        return `[${quote.text} (from ${sourceLabel})]`;
      });

      if (documentCropAttachment) {
        const cropRef = documentCropAttachment.page
          ? `[Image region from ${documentCropAttachment.documentName}, p.${documentCropAttachment.page}]`
          : `[Image region from ${documentCropAttachment.documentName}]`;
        finalMessage = `${finalMessage}\n\n${cropRef}`;
      }

      await sendAgentMessage(selectedAgentId, finalMessage, documentBase64, selectedModelId);
      setMessage('');
      setQuotes([]);
      setDocumentCropAttachment(null);
      nextQuoteIdRef.current = 0;
      await fetchDetail(true);
      setPollingNonce((prev) => prev + 1);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send message to agent');
    } finally {
      setSending(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedAgentId) return;
    try {
      setConfirming(true);
      await confirmAgent(selectedAgentId);
      toast.success('Agent confirmed – task created');
      await fetchDetail(true);
      setPollingNonce((prev) => prev + 1);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to confirm agent review');
    } finally {
      setConfirming(false);
    }
  };

  const handleCreateAgent = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message to create the agent');
      return;
    }

    const userMessage = {
      role: 'user',
      content: message.trim(),
      timestamp: new Date().toISOString(),
    };
    setCreationMessages([userMessage]);

    try {
      setCreating(true);

      const response = await createAgent({ message: message.trim() });
      toast.success('Agent created – processing…');

      const refreshedAgents = await listAgents();
      setAgents(refreshedAgents.data || []);

      const newAgentId = response.data?.id || refreshedAgents.data?.[0]?.id;

      if (newAgentId) {
        setSelectedAgentId(newAgentId);
        setCreatingNewAgent(false);
        setMessage('');
        setCreationMessages([]);
      } else {
        toast.error('Agent created but could not be selected. Please refresh.');
      }
    } catch (error) {
      const errMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to create agent';
      toast.error(errMsg);
    } finally {
      setCreating(false);
    }
  };

  return {
    message,
    setMessage,
    sending,
    confirming,
    creating,
    creationMessages,
    quotes,
    nextQuoteIdRef,
    cursorPositionRef,
    chatInputRef,
    documentCropAttachment,
    handleQuote,
    handleRemoveQuote,
    handleDocumentTextQuote,
    handleDocumentRegionCrop,
    handleRemoveCropAttachment,
    handleSendMessage,
    handleConfirm,
    handleCreateAgent,
  };
}
